// *** Angular imports
import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit, ChangeDetectorRef, Component, ContentChild,
  ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2, TemplateRef,
  ViewChild, forwardRef
} from '@angular/core';

// *** rxjs imports
import { Subject } from 'rxjs';

// *** Telerik imports
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { IndicatorsModule } from "@progress/kendo-angular-indicators";
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import { SVGIcon, downloadIcon, searchIcon, slidersIcon, xCircleIcon } from "@progress/kendo-svg-icons";

// *** Framework imports
import { ControlResizerOptions, ControlResizerService } from '../../../../../services/framework';
import { CriteriaContainerComponent, CriteriaSummaryBox, ICriteriaComponent, QueryPanelFilterBoxComponent } from 'src/app/modules/fwcore/components';
import { NotificationDialogService, QueryPanelConnectorService, QueryPanelContentConnectorService } from 'src/app/modules/fwcore/services';

@Component({
  selector: 'query-panel',
  standalone: true,
  styleUrls: ['./query-panel.component.scss'],
  templateUrl: './query-panel.component.html',
  imports: [
    ButtonsModule, IndicatorsModule, NgIf, NgTemplateOutlet, TooltipsModule,
    CriteriaSummaryBox, CriteriaContainerComponent,
    forwardRef(() => QueryPanelFilterBoxComponent)]
})

export class QueryPanelComponent
  implements OnDestroy, OnInit, AfterViewInit {
  /*-------------------------- Input / Outputs --------------------------------*/

  @Input() get caption(): string { return this._caption; }
  @Input() contentTemplate!: TemplateRef<any> | null;
  @Input() toolbarTemplate!: TemplateRef<any> | null;

  public set caption(caption: string) {
    this._caption = caption;
  }

  /*-------------------------- Inputs / Outputs --------------------*/

  @Input() public queryOnLoad: boolean = true;
  // buttons
  @Input() public showHeaderExcelButton: boolean = true;
  @Input() public showHeaderOpenCloseButton: boolean = true;
  @Input() public showHeaderSearchButton: boolean = false;
  @Input() public showSearch: boolean = true;
  @Input() public showFilterSearchButton: boolean = true;

  @Input() public searchButtonTooltip: string = "Search";
  @Input() public searchCaption: string = "Apply";

  @Input() public set showCriteriaPanel(val: boolean) {
    if (val) {
      this.gridClass = "grid";
      this.loadCriteria();
    } else {
      this.gridClass = "gHide";
    }
  }
  @Input() public set textFilter(val: string) {
    if (this.filterBox) {
      this.filterBox.value = val;
    }
    this.criteriaComponent.textFilter = val;
  }
  @Input() public set ignoreParameterChanges(val: string | null | undefined) {
    if (val && val != '') {
      this._ignoreList = val.split(',');
    } else {
      this._ignoreList = [];
    }
  }
  /*-------------------------- Internal properties  ---------------------------*/

  @ContentChild('criteria') criteriaComponent!: ICriteriaComponent;

  @ContentChild(CriteriaContainerComponent) treeViewContainer:
    CriteriaContainerComponent | undefined;

  @ViewChild(QueryPanelFilterBoxComponent) filterBox!: QueryPanelFilterBoxComponent;

  protected destroy$ = new Subject();
  protected filterStatusClass: string = "hide";
  protected filterStatusText: string = "";
  protected gridClass: string = "gHide";
  protected gridWidth: number = 100;
  protected paneHeight: number = 640;
  protected showCriteriaSummary: boolean = false;
  protected viewInit: boolean = false;

  // svg icons
  protected exportSvg: SVGIcon = downloadIcon;
  protected filterSvg: SVGIcon = slidersIcon;
  protected searchSvg: SVGIcon = searchIcon;
  protected xCircleSvg: SVGIcon = xCircleIcon;

  protected ccSub: any;
  protected subscription: any;

  private unlistener: () => void;
  protected _caption: string = "";

  // lazy loading
  protected loading: boolean = false;
  private _loadCount: number = -1;
  private _ignoreList: Array<string> = [];

  /*-------------------------- Constructor  --------------------------*/

  constructor (
    private _cdr: ChangeDetectorRef,
    protected controlResizer: ControlResizerService,
    protected connectorService: QueryPanelConnectorService,
    protected contentConnectorService: QueryPanelContentConnectorService,
    private readonly dialogService: NotificationDialogService,
    protected elem: ElementRef,
    private renderer: Renderer2
  ) {
    this.unlistener = function () {};
  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit(): void {
    this.viewInit = true;
    this.showCriteriaSummary = this.connectorService.hasObservers();

    // hide loading panel
    this.subscription = this.connectorService.onMessage().subscribe(message => {
      if (message) {
        switch (message.type) {
          case "isLoaded":
            --this._loadCount;
            if (this._loadCount < 1) {
              this.loading = false;
              this._cdr.detectChanges();
            }
            break;
          case "load":
            this.connectorService.isLoaded("_");
            break;
          case "add": case "delete":
            this.setFilterPending();
            break;
          case "clear":
            this.clearFilterStatus();
            break;
        }
      }
    });

    this.ccSub = this.contentConnectorService.onMessage().subscribe(message => {
      if (message) {
        switch (message.type) {
          case "search": case "export":
            if (this.haveCriteria(message.criteria)) {
              this.setFilterComplete();
            } else {
              this.clearFilterStatus();
            }
            this.sizeChange();
            break;
        }
      }
    });
  }

  ngAfterViewInit(): void {
    let topBarMenu = document.getElementsByClassName("topbar-menu");
    if (topBarMenu != undefined && topBarMenu.length > 0) {
      this.unlistener = this.renderer.listen(topBarMenu[0], "click",
        event => {
          this.sizeChange();
        });
    }
    // send a first search request
    if (this.queryOnLoad) {
      this.onSearchRequest();
      this._cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unlistener();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /*--------------------------- text filter --------------------------*/

  // change event for the filter text box (might not be needed?)
  protected onFilterChange(value: string) {
    this.criteriaComponent.textFilter = value;
    this.onSearchRequest();
  }

  /*--------------------------- filter status  --------------------------*/
  private haveCriteria(criteria: any): boolean {
    let property: keyof typeof criteria;

    for (property in criteria) {
      if ((property != "skip")
        && (property != "take")
        && (this._ignoreList.indexOf(property) == -1)) {
        if (criteria[property] !== false && criteria[property] !== "") {
          return true;
        }
      }
    }

    return false;
  }
  private clearFilterStatus() {
    this.filterStatusClass = "hide";
    this.filterStatusText = "";
  }

  private setFilterPending() {
    this.filterStatusClass = "dirty";
    this.filterStatusText = "\u25a0";
  }

  private setFilterComplete() {
    this.filterStatusClass = "clean";
    this.filterStatusText = "\u25cf";
  }
  /*--------------------------- buttons --------------------------*/

  protected onClearClick() {
    this.connectorService.clearItems();
    this.onSearchRequest();
  }

  // click event for the "show / hide filter" button
  protected onFilterShowHide(e: boolean) {
    // show on true, hide on false
    if (e) {
      this.gridClass = "grid";
      this.loadCriteria();
    } else {
      this.gridClass = "gHide";
    }
    this.sizeChange();
  }

  private areCriteriaValid(): boolean {
    if (this.criteriaComponent && this.criteriaComponent.isValid()) {
      return true;
    }

    this.dialogService.openNotificationDialog("Error",
      "One or more required search criteria are missing, or not valid.");
    return false;
  }
  public onExportRequest() {
    if (this.areCriteriaValid()) {
      let params = this.criteriaComponent.getParameters();
      let headerLines = this.criteriaComponent.getHeaderLines();
      this.contentConnectorService.export(params, this._caption, headerLines);
    }
  }

  public onSearchRequest() {
    if (this.areCriteriaValid()) {
      let criteria = this.criteriaComponent.getParameters();
      this.contentConnectorService.search(criteria);
    }
  }

  /*--------------------------- lazy loading --------------------------*/

  protected loadCriteria(): void {
    if (this._loadCount < 0) {
      this._loadCount = this.connectorService.observerCount;
      this.loading = (this._loadCount > 0);
      this.connectorService.loadAll();
    }
  }

  /*--------------------------- resize --------------------------*/

  @HostListener('window:resize', ['$event'])
  protected onResize(event: Event) {
    this.sizeChange();
  }

  protected sizeChange() {
    if (this.viewInit) {
      // calculate width
      if (this.elem.nativeElement) {
        // Get gap between menu panel and query panel
        let offsetLeft = this.elem.nativeElement.offsetLeft;

        // Get width of menu panel
        let menuOffset = 0;
        let menuPanel = document.querySelector(".isgmenupanel");
        if (menuPanel != undefined && menuPanel != null) {
          menuOffset = menuPanel.getBoundingClientRect().width;
        }

        // Get width of the right hand side
        let rightPanel = this.elem.nativeElement.querySelector(".right");
        let rightWidth = 10;
        if (this.gridClass == "grid" && rightPanel != undefined && rightPanel != null) {
          rightWidth = 350;
        }

        // Calculate the width of the grid
        this.gridWidth = window.innerWidth - (offsetLeft * 2) - menuOffset - rightWidth; // - 109;

        // calculate maximum height of the right hand panel
        let options: ControlResizerOptions = new ControlResizerOptions();
        options.offsetTopHeight = 70;
        options.offsetBottomHeight = 10;
        this.paneHeight = this.controlResizer.calculateHeight(options);
      }
    }
  }
}
